import { useMemo } from 'react'
import { useOrionDomainQuery } from '../../../../../../api/coreData'
import { transformResponse } from '../../../lib/transformResponse'
import { findRowByIdentity } from '../../lib/findRowByIdentity'

// Single-grouping React Query hook for the allocation tool. Reuses the
// grid's `execute-orion-domain-query` proxy (and its existing React
// Query key namespace `['orion/domain-query', ...]`), so the cache is
// shared and a clientId / userId change naturally invalidates both.
// Each unique request body is cached independently, so re-opening the
// tool with the same payload is instant.

export function useAllocationQuery ({ request, clientId, columnInstances, globalDateConfig, entity, entityIds, unwrap = null }) {
  const queryResult = useOrionDomainQuery(
    'reporting/scope',
    clientId,
    request,
    { enabled: !!request && !!clientId }
  )

  const { data: rawData, isFetching, error, refetch } = queryResult

  const { items, totalRow } = useMemo(() => {
    if (!rawData) return { items: [], totalRow: null }
    // hasMoreLevels: false -- the synthesized config has exactly one
    // grouping, so there are no deeper levels to lazy-load. Rows come
    // back fully populated with no `_placeholder` children.
    const { rows, totalRow: total } = transformResponse(rawData, {
      hasMoreLevels: false,
      columnInstances,
      globalDateConfig,
      entity,
      entityIds
    })
    // For filtered-child requests (virtual-grouping row scope), the
    // response is wrapped in the outer grouping the filter targets.
    // Mirror the unwrap fetchRowChildren does in
    // usePortfolioExplorerQuery.js: find the row that matches the
    // identity we charted on, and use its .data as the actual items.
    // Strict id-then-name semantics via the shared helper (no
    // name-fallback when groupEntityId is provided -- two siblings
    // sharing a display name would have produced a silent false
    // positive under the old `||` shape).
    if (unwrap) {
      const match = findRowByIdentity(rows, unwrap)
      const innerRows = Array.isArray(match?.data) ? match.data : []
      return { items: innerRows, totalRow: total }
    }
    return { items: rows, totalRow: total }
  }, [rawData, columnInstances, globalDateConfig, entity, entityIds, unwrap])

  return {
    items,
    totalRow,
    isFetching,
    error,
    refetch
  }
}
